import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import {
    Phone,
    ArrowRight,
    Check,
    MapPin,
    Award,
    Clock,
    ShieldCheck,
    Sparkles,
    MessageCircle,
    Square,
    Bath,
    Shield,
    Waves,
    LayoutGrid,
    Ruler,
    Home,
    Hammer,
    Wrench,
    CheckCircle,
    CloudRain,
    Layers,
    Droplet,
    Grid3x3,
    Sun,
    Minimize2,
    Repeat,
    Palette,
    Mail,
    Send,
    Loader2,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useSettings } from '@/lib/settings';
import { faqSchema } from '@/components/SEO';
import { formatDate } from '@/lib/utils';

const ICONS = {
    'check-circle': CheckCircle,
    'map-pin': MapPin,
    award: Award,
    clock: Clock,
    'shield-check': ShieldCheck,
    sparkles: Sparkles,
    'message-circle': MessageCircle,
    square: Square,
    bath: Bath,
    shield: Shield,
    waves: Waves,
    'layout-grid': LayoutGrid,
    ruler: Ruler,
    home: Home,
    hammer: Hammer,
    wrench: Wrench,
    tool: Wrench,
    'cloud-rain': CloudRain,
    layers: Layers,
    droplet: Droplet,
    'grid-3x3': Grid3x3,
    sun: Sun,
    'minimize-2': Minimize2,
    repeat: Repeat,
    palette: Palette,
};

function Icon({ name, className = 'h-5 w-5' }) {
    const Component = ICONS[name] || Check;
    return <Component className={className} />;
}

// =============== HERO ===============
export function HeroSection({ data, isFirst }) {
    const bg = data.background_image;
    return (
        <section
            data-testid="home-hero"
            className="relative overflow-hidden"
        >
            {bg && (
                <>
                    <img
                        src={bg}
                        alt={data.image_alt || 'Hero'}
                        className="absolute inset-0 h-full w-full object-cover"
                        fetchpriority={isFirst ? 'high' : 'auto'}
                        decoding={isFirst ? 'sync' : 'async'}
                        loading={isFirst ? 'eager' : 'lazy'}
                        width="2000"
                        height="1200"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/35 to-black/55" />
                </>
            )}
            <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-24 pb-16 sm:pt-32 sm:pb-20 lg:pt-40 lg:pb-28">
                <div className="max-w-3xl">
                    {data.eyebrow && (
                        <p className="text-xs sm:text-sm uppercase tracking-[0.18em] text-white/85 mb-5">
                            {data.eyebrow}
                        </p>
                    )}
                    <h1
                        className="font-display text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium leading-[1.05] tracking-tight"
                        data-testid="hero-heading"
                    >
                        {data.heading}
                    </h1>
                    {data.subheading && (
                        <p className="mt-6 text-base sm:text-lg lg:text-xl text-white/90 leading-relaxed max-w-2xl">
                            {data.subheading}
                        </p>
                    )}
                    <div className="mt-8 flex flex-wrap gap-3">
                        {data.primary_cta && (
                            <Button
                                asChild
                                size="lg"
                                className="bg-white text-[color:var(--sk-text)] hover:bg-white/90 rounded-xl h-12 px-5 sm:px-7 text-sm sm:text-base font-medium"
                                data-testid="home-hero-call-button"
                            >
                                <a href={data.primary_cta.href}>{data.primary_cta.label}</a>
                            </Button>
                        )}
                        {data.secondary_cta && (
                            <Button
                                asChild
                                size="lg"
                                variant="outline"
                                className="bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white rounded-xl h-12 px-5 sm:px-7 text-sm sm:text-base"
                                data-testid="home-hero-references-button"
                            >
                                <Link to={data.secondary_cta.href}>
                                    {data.secondary_cta.label}
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                        )}
                    </div>
                    {data.trust && data.trust.length > 0 && (
                        <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/85" data-testid="hero-trust">
                            {data.trust.map((t, i) => (
                                <li key={i} className="flex items-center gap-2">
                                    <Icon name={t.icon} className="h-4 w-4 text-[color:var(--sk-accent-2)]" />
                                    <span>{t.text}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    );
}

// =============== PAGE HEADER (smaller hero for sub-pages) ===============
export function PageHeaderSection({ data }) {
    const bg = data.background_image;
    return (
        <section className="relative overflow-hidden border-b border-border" data-testid="page-header">
            {bg && (
                <>
                    <img
                        src={bg}
                        alt={data.image_alt || ''}
                        className="absolute inset-0 h-full w-full object-cover"
                        fetchpriority="high"
                        decoding="sync"
                        loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/45 to-black/60" />
                </>
            )}
            <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-24 pb-16 sm:pt-28 sm:pb-20 lg:pt-32 lg:pb-24">
                <div className="max-w-3xl">
                    {data.eyebrow && (
                        <p className="text-xs sm:text-sm uppercase tracking-[0.18em] text-white/85 mb-4">
                            {data.eyebrow}
                        </p>
                    )}
                    <h1 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
                        {data.heading}
                    </h1>
                    {data.subheading && (
                        <p className="mt-4 text-base sm:text-lg text-white/85 max-w-2xl">{data.subheading}</p>
                    )}
                    {(data.primary_cta || data.secondary_cta) && (
                        <div className="mt-6 flex flex-wrap gap-3">
                            {data.primary_cta && (
                                <Button asChild size="lg" className="bg-white text-[color:var(--sk-text)] hover:bg-white/90 rounded-xl">
                                    <a href={data.primary_cta.href}>{data.primary_cta.label}</a>
                                </Button>
                            )}
                            {data.secondary_cta && (
                                <Button asChild size="lg" variant="outline" className="bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white rounded-xl">
                                    <Link to={data.secondary_cta.href}>{data.secondary_cta.label}</Link>
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

// =============== TRUST SIGNALS (small inline strip) ===============
export function TrustSection({ data }) {
    const items = data.items || [];
    if (items.length === 0) return null;
    return (
        <section className="sk-section" data-testid="trust-section">
            <div className="sk-container">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {items.map((it, i) => (
                        <div key={i} className="flex items-start gap-3 rounded-xl border border-border bg-white p-4">
                            <Icon name={it.icon} className="h-5 w-5 text-[color:var(--sk-accent)] shrink-0 mt-0.5" />
                            <span className="text-sm font-medium">{it.text}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// =============== INTRO (text + image) ===============
export function IntroSection({ data, reverse = false }) {
    return (
        <section className="sk-section" data-testid="intro-section">
            <div className="sk-container">
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? 'lg:[&>div:first-child]:order-2' : ''}`}>
                    <div>
                        {data.eyebrow && <p className="sk-kicker">{data.eyebrow}</p>}
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 mb-5 leading-tight">
                            {data.heading}
                        </h2>
                        <p className="text-base sm:text-lg text-foreground/75 leading-relaxed">
                            {data.text}
                        </p>
                        {data.cta && (
                            <Button
                                asChild
                                variant="outline"
                                className="mt-6 rounded-xl border-border bg-white"
                            >
                                <Link to={data.cta.href}>
                                    {data.cta.label}
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                        )}
                    </div>
                    {data.image && (
                        <div className="relative rounded-2xl overflow-hidden border border-border">
                            <div className="aspect-[4/3]">
                                <img
                                    src={data.image}
                                    alt={data.image_alt || ''}
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

// =============== SERVICES GRID ===============
export function ServicesGridSection({ data }) {
    const [services, setServices] = useState([]);
    useEffect(() => {
        let mounted = true;
        api.get('/public/services').then(({ data: srv }) => {
            if (mounted) {
                let filtered = srv;
                if (data.limit_slugs && data.limit_slugs.length) {
                    filtered = srv.filter((s) => data.limit_slugs.includes(s.slug));
                }
                setServices(filtered);
            }
        });
        return () => { mounted = false; };
    }, [data.limit_slugs]);

    return (
        <section className="sk-section bg-[color:var(--sk-surface-2)]" data-testid="services-grid-section">
            <div className="sk-container">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    {data.eyebrow && <p className="sk-kicker">{data.eyebrow}</p>}
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 mb-4">{data.heading}</h2>
                    {data.subheading && (
                        <p className="text-base sm:text-lg text-foreground/70">{data.subheading}</p>
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
                    {services.map((service) => {
                        const href = service.page_slug ? `/${service.page_slug}` : '/usluge';
                        return (
                            <Link
                                key={service.id}
                                to={href}
                                className="group block overflow-hidden rounded-2xl border border-border bg-white shadow-[var(--sk-shadow-sm)] hover:shadow-[var(--sk-shadow-md)] transition-shadow"
                                data-testid="services-card"
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img
                                        src={service.featured_image}
                                        alt={service.title}
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center gap-3">
                                        <Icon name={service.icon} className="h-5 w-5 text-[color:var(--sk-accent)]" />
                                        <h3 className="font-display text-xl sm:text-2xl tracking-tight">{service.title}</h3>
                                    </div>
                                    <p className="mt-3 text-sm text-foreground/70 leading-relaxed line-clamp-2">
                                        {service.short_description}
                                    </p>
                                    <span
                                        className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-foreground group-hover:text-[color:var(--sk-accent)] transition-colors"
                                        data-testid="services-card-learn-more-link"
                                    >
                                        Saznaj više
                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                                    </span>
                                </div>
                            </Link>
                        );
                    })}
                </div>
                {data.cta_all && (
                    <div className="text-center mt-10">
                        <Button asChild variant="outline" className="rounded-xl bg-white">
                            <Link to={data.cta_all.href}>
                                {data.cta_all.label}
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
}

// =============== WHY US ===============
export function WhyUsSection({ data }) {
    return (
        <section className="sk-section" data-testid="why-us-section">
            <div className="sk-container">
                <div className="max-w-2xl mb-12">
                    {data.eyebrow && <p className="sk-kicker">{data.eyebrow}</p>}
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2">{data.heading}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(data.items || []).map((item, i) => (
                        <div key={i} className="rounded-2xl border border-border bg-white p-6">
                            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[color:var(--sk-surface-2)]">
                                <Icon name={item.icon} className="h-5 w-5 text-[color:var(--sk-accent)]" />
                            </div>
                            <h3 className="mt-4 text-lg font-medium">{item.title}</h3>
                            <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{item.text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// =============== REFERENCES ===============
export function ReferencesSection({ data }) {
    const [projects, setProjects] = useState([]);
    useEffect(() => {
        api.get('/public/projects').then(({ data: ps }) => {
            const limit = data.limit || ps.length;
            setProjects(ps.slice(0, limit));
        }).catch(() => {});
    }, [data.limit]);

    if (projects.length === 0) return null;
    return (
        <section className="sk-section" data-testid="references-section">
            <div className="sk-container">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        {data.eyebrow && <p className="sk-kicker">{data.eyebrow}</p>}
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2">{data.heading}</h2>
                        {data.subheading && (
                            <p className="mt-3 text-foreground/70 max-w-xl">{data.subheading}</p>
                        )}
                    </div>
                    {data.cta_all && (
                        <Button asChild variant="ghost" className="hidden sm:inline-flex">
                            <Link to={data.cta_all.href}>
                                {data.cta_all.label}
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                        </Button>
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="references-grid">
                    {projects.map((p) => (
                        <Link
                            key={p.id}
                            to={`/reference/${p.slug}`}
                            className="group relative overflow-hidden rounded-2xl border border-border bg-white"
                            data-testid="references-card"
                        >
                            <div className="aspect-[4/3] overflow-hidden">
                                <img
                                    src={p.featured_image}
                                    alt={p.title}
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-95" />
                            <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-white/80">
                                    {p.location} · {p.year}
                                </p>
                                <h3 className="mt-1 font-display text-xl sm:text-2xl tracking-tight">{p.title}</h3>
                                <p className="mt-1 text-xs text-white/80">{p.type_of_work}</p>
                            </div>
                        </Link>
                    ))}
                </div>
                {data.cta_all && (
                    <div className="text-center mt-10 sm:hidden">
                        <Button asChild variant="outline" className="rounded-xl bg-white">
                            <Link to={data.cta_all.href}>{data.cta_all.label}</Link>
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
}

// =============== PROCESS ===============
export function ProcessSection({ data }) {
    return (
        <section className="sk-section bg-[color:var(--sk-surface-2)]" data-testid="process-steps">
            <div className="sk-container">
                <div className="max-w-2xl mb-10">
                    {data.eyebrow && <p className="sk-kicker">{data.eyebrow}</p>}
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2">{data.heading}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                    {(data.steps || []).map((step, i) => (
                        <div
                            key={i}
                            className="rounded-2xl border border-border bg-white p-5 flex flex-col"
                            data-testid="process-step"
                        >
                            <span className="text-xs font-mono text-[color:var(--sk-accent)] tracking-widest">
                                {step.number}
                            </span>
                            <h3 className="mt-3 font-medium text-base">{step.title}</h3>
                            <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{step.text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// =============== FAQ ===============
export function FAQSection({ data }) {
    const items = data.items || [];
    if (items.length === 0) return null;
    const schema = faqSchema(items);
    return (
        <section className="sk-section" data-testid="faq">
            {schema && (
                <Helmet>
                    <script type="application/ld+json">{JSON.stringify(schema)}</script>
                </Helmet>
            )}
            <div className="sk-container max-w-3xl">
                <div className="text-center mb-10">
                    {data.eyebrow && <p className="sk-kicker">{data.eyebrow}</p>}
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2">{data.heading}</h2>
                </div>
                <Accordion type="single" collapsible className="space-y-3">
                    {items.map((it, i) => (
                        <AccordionItem
                            key={i}
                            value={`item-${i}`}
                            className="rounded-2xl border border-border bg-white px-5"
                            data-testid="faq-item"
                        >
                            <AccordionTrigger className="py-5 text-left text-base font-medium hover:no-underline">
                                {it.question}
                            </AccordionTrigger>
                            <AccordionContent className="pb-5 text-sm text-foreground/70 leading-relaxed">
                                {it.answer}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
}

// =============== CTA ===============
export function CTASection({ data }) {
    return (
        <section className="sk-section relative overflow-hidden" data-testid="cta-section">
            {data.background_image && (
                <>
                    <img
                        src={data.background_image}
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                        decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/65 to-black/75" />
                </>
            )}
            <div className="relative sk-container text-center">
                <h2 className={`font-display text-3xl sm:text-4xl lg:text-5xl ${data.background_image ? 'text-white' : 'text-foreground'}`}>
                    {data.heading}
                </h2>
                {data.subheading && (
                    <p className={`mt-4 text-base sm:text-lg max-w-2xl mx-auto ${data.background_image ? 'text-white/85' : 'text-foreground/70'}`}>
                        {data.subheading}
                    </p>
                )}
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                    {data.primary_cta && (
                        <Button asChild size="lg" className="bg-white text-[color:var(--sk-text)] hover:bg-white/90 rounded-xl">
                            <a href={data.primary_cta.href}>
                                <Phone className="mr-2 h-4 w-4" />
                                {data.primary_cta.label}
                            </a>
                        </Button>
                    )}
                    {data.secondary_cta && (
                        <Button
                            asChild
                            size="lg"
                            variant="outline"
                            className={`rounded-xl ${data.background_image ? 'bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white' : 'bg-white'}`}
                        >
                            <Link to={data.secondary_cta.href}>{data.secondary_cta.label}</Link>
                        </Button>
                    )}
                </div>
            </div>
        </section>
    );
}

// =============== BLOG PREVIEW ===============
export function BlogPreviewSection({ data }) {
    const [posts, setPosts] = useState([]);
    useEffect(() => {
        api.get('/public/blog').then(({ data: ps }) => {
            const limit = data.limit || 3;
            setPosts(ps.slice(0, limit));
        }).catch(() => {});
    }, [data.limit]);

    if (posts.length === 0) return null;
    return (
        <section className="sk-section" data-testid="blog-preview-section">
            <div className="sk-container">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        {data.eyebrow && <p className="sk-kicker">{data.eyebrow}</p>}
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2">{data.heading}</h2>
                        {data.subheading && (
                            <p className="mt-3 text-foreground/70 max-w-xl">{data.subheading}</p>
                        )}
                    </div>
                    {data.cta_all && (
                        <Button asChild variant="ghost" className="hidden sm:inline-flex">
                            <Link to={data.cta_all.href}>
                                {data.cta_all.label}
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                        </Button>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="blog-preview-grid">
                    {posts.map((post) => (
                        <Link
                            key={post.id}
                            to={`/blog/${post.slug}`}
                            className="group overflow-hidden rounded-2xl border border-border bg-white"
                            data-testid="blog-preview-card"
                        >
                            <div className="aspect-[16/10] overflow-hidden">
                                <img
                                    src={post.featured_image}
                                    alt={post.title}
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div className="p-6">
                                {post.categories && post.categories[0] && (
                                    <Badge variant="secondary" className="bg-[color:var(--sk-surface-2)] text-foreground/80">
                                        {post.categories[0]}
                                    </Badge>
                                )}
                                <h3 className="mt-3 font-display text-xl sm:text-2xl tracking-tight">{post.title}</h3>
                                <p className="mt-2 text-sm text-foreground/70 leading-relaxed line-clamp-3">{post.excerpt}</p>
                                <p className="mt-5 text-xs uppercase tracking-[0.18em] text-foreground/55">
                                    {formatDate(post.published_at)}
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

// =============== GALLERY ===============
export function GallerySection({ data }) {
    const images = data.images || [];
    if (images.length === 0) return null;
    return (
        <section className="sk-section bg-[color:var(--sk-surface-2)]" data-testid="gallery-section">
            <div className="sk-container">
                <div className="max-w-2xl mb-10">
                    {data.eyebrow && <p className="sk-kicker">{data.eyebrow}</p>}
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2">{data.heading}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {images.map((img, i) => (
                        <div key={i} className="aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-white">
                            <img
                                src={img.url}
                                alt={img.alt || ''}
                                className="h-full w-full object-cover hover:scale-[1.04] transition-transform duration-700"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// =============== CONTACT FORM ===============
export function ContactFormSection() {
    const { settings } = useSettings();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: '', email: '', phone: '', subject: '', message: '', consent: true, website: ''
    });

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error('Molimo ispunite ime, email i poruku');
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post('/contact', form);
            toast.success(data.message || 'Poruka je poslana!');
            setForm({ name: '', email: '', phone: '', subject: '', message: '', consent: true, website: '' });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Slanje nije uspjelo. Pokušajte ponovno.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="sk-section" data-testid="contact-section">
            <div className="sk-container">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                    {/* Contact info */}
                    <div>
                        <p className="sk-kicker">Kontakt</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 mb-6">Tu smo za vas</h2>
                        <p className="text-foreground/70 leading-relaxed">
                            Javite se telefonom za brzu reakciju ili nam pošaljite poruku – odgovaramo u najkraćem
                            mogućem roku, tijekom radnog vremena.
                        </p>
                        <ul className="mt-8 space-y-4">
                            <li className="flex items-start gap-4 rounded-2xl border border-border bg-white p-4">
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--sk-surface-2)]">
                                    <Phone className="h-5 w-5 text-[color:var(--sk-accent)]" />
                                </div>
                                <div>
                                    <p className="sk-kicker">Telefon</p>
                                    <a
                                        href={settings?.phone_link || 'tel:+385977094158'}
                                        className="mt-1 block font-medium text-lg"
                                        data-testid="contact-phone-link"
                                    >
                                        {settings?.phone || '+385 97 709 4158'}
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-4 rounded-2xl border border-border bg-white p-4">
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--sk-surface-2)]">
                                    <Mail className="h-5 w-5 text-[color:var(--sk-accent)]" />
                                </div>
                                <div>
                                    <p className="sk-kicker">E-mail</p>
                                    <a
                                        href={`mailto:${settings?.email || 'skobickeramika@gmail.com'}`}
                                        className="mt-1 block font-medium text-lg break-all"
                                        data-testid="contact-email-link"
                                    >
                                        {settings?.email || 'skobickeramika@gmail.com'}
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-4 rounded-2xl border border-border bg-white p-4">
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--sk-surface-2)]">
                                    <MapPin className="h-5 w-5 text-[color:var(--sk-accent)]" />
                                </div>
                                <div>
                                    <p className="sk-kicker">Lokacija</p>
                                    <p className="mt-1 font-medium text-lg">
                                        {settings?.address || 'Poreč, Istra, Hrvatska'}
                                    </p>
                                    {settings?.working_hours && (
                                        <p className="mt-1 text-sm text-foreground/65">{settings.working_hours}</p>
                                    )}
                                </div>
                            </li>
                        </ul>
                        {settings?.google_maps_embed && (
                            <div className="mt-6 rounded-2xl overflow-hidden border border-border">
                                <div
                                    dangerouslySetInnerHTML={{ __html: settings.google_maps_embed }}
                                    data-testid="contact-map"
                                />
                            </div>
                        )}
                    </div>

                    {/* Form */}
                    <form
                        onSubmit={onSubmit}
                        className="rounded-2xl border border-border bg-white p-6 sm:p-8 shadow-[var(--sk-shadow-sm)]"
                        data-testid="contact-form"
                    >
                        <h3 className="font-display text-2xl sm:text-3xl mb-2">Pošaljite upit</h3>
                        <p className="text-sm text-foreground/65 mb-6">Odgovor obično u istom danu.</p>
                        {/* Honeypot */}
                        <input
                            type="text"
                            value={form.website}
                            onChange={(e) => set('website', e.target.value)}
                            className="hidden"
                            autoComplete="off"
                            tabIndex={-1}
                            aria-hidden="true"
                        />
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="name">Ime i prezime <span className="text-[color:var(--sk-accent)]">*</span></Label>
                                <Input
                                    id="name"
                                    value={form.name}
                                    onChange={(e) => set('name', e.target.value)}
                                    className="mt-2 rounded-xl bg-white"
                                    required
                                    data-testid="contact-form-name-input"
                                />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="email">E-mail <span className="text-[color:var(--sk-accent)]">*</span></Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => set('email', e.target.value)}
                                        className="mt-2 rounded-xl bg-white"
                                        required
                                        data-testid="contact-form-email-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="phone">Telefon</Label>
                                    <Input
                                        id="phone"
                                        value={form.phone}
                                        onChange={(e) => set('phone', e.target.value)}
                                        className="mt-2 rounded-xl bg-white"
                                        data-testid="contact-form-phone-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="subject">Tema</Label>
                                <Input
                                    id="subject"
                                    value={form.subject}
                                    onChange={(e) => set('subject', e.target.value)}
                                    className="mt-2 rounded-xl bg-white"
                                    placeholder="Npr. Adaptacija kupaonice"
                                />
                            </div>
                            <div>
                                <Label htmlFor="message">Poruka <span className="text-[color:var(--sk-accent)]">*</span></Label>
                                <Textarea
                                    id="message"
                                    value={form.message}
                                    onChange={(e) => set('message', e.target.value)}
                                    rows={5}
                                    className="mt-2 rounded-xl bg-white"
                                    placeholder="Opišite kratko što vam treba…"
                                    required
                                    data-testid="contact-form-message-textarea"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="w-full sm:w-auto bg-[color:var(--sk-text)] text-white hover:bg-black rounded-xl h-12 px-7"
                                data-testid="contact-form-submit-button"
                            >
                                {submitting ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Slanje…</>
                                ) : (
                                    <><Send className="mr-2 h-4 w-4" /> Pošalji poruku</>
                                )}
                            </Button>
                            <p className="text-xs text-foreground/55">
                                Slanjem prihvaćate kontakt putem ostavljenih podataka. Ne dijelimo vaše podatke.
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

// =============== CUSTOM HTML ===============
export function CustomHTMLSection({ data, customCss }) {
    return (
        <section className="sk-section" data-testid="custom-html-section">
            <div className="sk-container">
                {customCss && <style dangerouslySetInnerHTML={{ __html: customCss }} />}
                <div dangerouslySetInnerHTML={{ __html: data.html || '' }} />
            </div>
        </section>
    );
}

// =============== SECTION RENDERER ===============
export function SectionRenderer({ section, index }) {
    if (!section || section.enabled === false) return null;
    const isFirst = index === 0;
    const wrapperStyle = section.custom_css ? (
        <style dangerouslySetInnerHTML={{ __html: section.custom_css }} />
    ) : null;

    let content;
    switch (section.type) {
        case 'hero':
            content = <HeroSection data={section.data || {}} isFirst={isFirst} />; break;
        case 'page_header':
            content = <PageHeaderSection data={section.data || {}} />; break;
        case 'trust':
            content = <TrustSection data={section.data || {}} />; break;
        case 'intro':
            content = <IntroSection data={section.data || {}} />; break;
        case 'services_grid':
            content = <ServicesGridSection data={section.data || {}} />; break;
        case 'why_us':
            content = <WhyUsSection data={section.data || {}} />; break;
        case 'references':
            content = <ReferencesSection data={section.data || {}} />; break;
        case 'process':
            content = <ProcessSection data={section.data || {}} />; break;
        case 'faq':
            content = <FAQSection data={section.data || {}} />; break;
        case 'cta':
            content = <CTASection data={section.data || {}} />; break;
        case 'blog_preview':
            content = <BlogPreviewSection data={section.data || {}} />; break;
        case 'gallery':
            content = <GallerySection data={section.data || {}} />; break;
        case 'contact_form':
            content = <ContactFormSection />; break;
        case 'custom_html':
            content = <CustomHTMLSection data={section.data || {}} customCss={section.custom_css} />; break;
        default:
            content = null;
    }
    return (
        <>
            {wrapperStyle}
            {content}
        </>
    );
}

export default SectionRenderer;
